import React, { useContext } from 'react';
import cheksteLogo from '../assets/chekste_logo_horizontal_web.png';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { UserContext } from '../App';
import Header from '../components/Header';
import { startLogin } from '../utils/authProvider';
import { ArrowRight, TrendingUp, DollarSign, BarChart3, Shield, Zap, Check } from 'lucide-react';

const LandingPage = () => {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  // Handler for auth-required actions using mobile-safe storage
  const handleAuthAction = (redirectTo = '/analyze') => {
    if (user) {
      navigate(redirectTo);
    } else {
      // Provider (emergent | google) resolved at runtime from /api/auth/config.
      startLogin(redirectTo);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header variant="landing" />

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-20 px-4 sm:px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-primary mb-6 leading-tight">
              {lang === 'en' 
                ? 'Analyze Real Estate Deals in Seconds' 
                : 'Analiza Tratos Inmobiliarios en Segundos'}
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto mb-4 leading-relaxed px-4">
              {lang === 'en' 
                ? 'Know if a deal works, why it works, and how to improve it — before you risk your money.' 
                : 'Sabe si un trato funciona, por qué funciona, y cómo mejorarlo — antes de arriesgar tu dinero.'}
            </p>
            <p className="text-sm text-slate-500 mb-8">
              {lang === 'en' 
                ? 'Powered by real market data, not guesswork.' 
                : 'Impulsado por datos reales del mercado, no suposiciones.'}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8">
              <button
                onClick={() => navigate('/analyze')}
                className="w-full sm:w-auto px-8 py-4 bg-secondary text-white rounded-lg font-semibold hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl active:scale-95 flex items-center justify-center gap-2"
                data-testid="hero-cta"
              >
                {lang === 'en' ? 'Start Analyzing Deals' : 'Comenzar a Analizar Tratos'}
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('/pricing')}
                className="w-full sm:w-auto px-8 py-4 bg-white text-slate-900 border border-slate-200 rounded-lg font-semibold hover:bg-slate-50 transition-all shadow-sm"
              >
                {lang === 'en' ? 'View Pricing' : 'Ver Precios'}
              </button>
            </div>
            
            {/* Value Props */}
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-500" />
                <span>{lang === 'en' ? 'Real market data' : 'Datos reales del mercado'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" />
                <span>{lang === 'en' ? 'Results in seconds' : 'Resultados en segundos'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-indigo-500" />
                <span>{lang === 'en' ? 'No credit card required' : 'Sin tarjeta de crédito'}</span>
              </div>
            </div>
          </div>
          
          {/* Hero Image */}
          <div className="mt-12 sm:mt-16 rounded-2xl overflow-hidden shadow-2xl border border-border mx-auto max-w-4xl">
            <img 
              src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&h=600&fit=crop" 
              alt="Real Estate Investment"
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-primary text-center mb-4">
            {lang === 'en' ? 'Make Faster, Safer Investment Decisions' : 'Toma Decisiones de Inversión Más Rápidas y Seguras'}
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            {lang === 'en' 
              ? 'Stop guessing if a deal works. Get instant validation with real market data.' 
              : 'Deja de adivinar si un trato funciona. Obtén validación instantánea con datos reales.'}
          </p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            <div className="text-center p-6 bg-slate-50 rounded-xl">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-secondary" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3">
                {lang === 'en' ? '1. Enter Any Property' : '1. Ingresa Cualquier Propiedad'}
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base">
                {lang === 'en' 
                  ? 'Paste a Zillow, Redfin, or Realtor link, or enter any US property address.'
                  : 'Pega un enlace de Zillow, Redfin o Realtor, o ingresa cualquier dirección en EE.UU.'}
              </p>
            </div>
            <div className="text-center p-6 bg-slate-50 rounded-xl">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3">
                {lang === 'en' ? '2. Get Instant Analysis' : '2. Obtén Análisis Instantáneo'}
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base">
                {lang === 'en'
                  ? 'ARV, rehab estimate, profit projections, and deal classification—all in seconds.'
                  : 'ARV, estimación de remodelación, proyecciones de ganancia y clasificación de trato—todo en segundos.'}
              </p>
            </div>
            <div className="text-center p-6 bg-slate-50 rounded-xl sm:col-span-2 md:col-span-1">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-indigo-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3">
                {lang === 'en' ? '3. Know Before You Invest' : '3. Sabe Antes de Invertir'}
              </h3>
              <p className="text-muted-foreground text-sm sm:text-base">
                {lang === 'en'
                  ? 'Clear recommendation: GOOD DEAL, MARGINAL, or NO PASS. No guesswork.'
                  : 'Recomendación clara: BUEN TRATO, MARGINAL, o NO APROBADO. Sin conjeturas.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section - Simple CTA to full pricing page */}
      <section id="pricing" className="py-16 sm:py-20 px-4 sm:px-6 bg-slate-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-primary mb-4">
            {lang === 'en' ? 'Simple, Transparent Pricing' : 'Precios Simples y Transparentes'}
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            {lang === 'en' 
              ? 'Start free with 3 analyses. Upgrade when you need more. No hidden fees.' 
              : 'Comienza gratis con 3 análisis. Mejora cuando necesites más. Sin costos ocultos.'}
          </p>
          
          {/* Quick Plan Overview */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8 max-w-3xl mx-auto">
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <div className="text-2xl font-bold text-slate-900">Free</div>
              <div className="text-3xl font-bold text-blue-600 mt-1">3</div>
              <div className="text-sm text-slate-500">{lang === 'en' ? 'lifetime analyses' : 'análisis de por vida'}</div>
            </div>
            <div className="bg-blue-600 text-white rounded-xl p-5 shadow-lg shadow-blue-200">
              <div className="text-2xl font-bold">Investor</div>
              <div className="text-3xl font-bold mt-1">50</div>
              <div className="text-sm text-blue-100">{lang === 'en' ? 'analyses per month' : 'análisis por mes'}</div>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <div className="text-2xl font-bold text-slate-900">Pro</div>
              <div className="text-3xl font-bold text-blue-600 mt-1">200</div>
              <div className="text-sm text-slate-500">{lang === 'en' ? 'analyses per month' : 'análisis por mes'}</div>
            </div>
          </div>
          
          <button
            onClick={() => navigate('/pricing')}
            className="px-8 py-4 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-all shadow-lg inline-flex items-center gap-2"
          >
            {lang === 'en' ? 'View All Plans' : 'Ver Todos los Planes'}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 bg-primary text-primary-foreground">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            {lang === 'en' ? 'Stop Guessing. Start Knowing.' : 'Deja de Adivinar. Comienza a Saber.'}
          </h2>
          <p className="text-lg sm:text-xl opacity-90 mb-8">
            {lang === 'en' 
              ? 'Every minute you spend analyzing a bad deal manually is a minute wasted. Get instant validation.'
              : 'Cada minuto que pasas analizando un mal trato manualmente es un minuto perdido. Obtén validación instantánea.'}
          </p>
          <button
            onClick={() => navigate('/analyze')}
            className="px-8 py-4 bg-white text-primary rounded-lg font-semibold hover:bg-slate-50 transition-all shadow-lg inline-flex items-center gap-2"
          >
            {lang === 'en' ? 'Analyze Your First Deal Free' : 'Analiza Tu Primer Trato Gratis'}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 sm:py-12 px-4 sm:px-6 bg-slate-50 border-t border-border">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center">
              {/* The horizontal lockup replaces BOTH the placeholder glyph and the
                  wordmark beside it — it already contains the word, so keeping the
                  text span would print CHEKSTE twice. Dark artwork on bg-slate-50.
                  h-7 keeps the footer's optical weight: the old mark was h-6 plus a
                  text label, and the lockup carries both. */}
              <img
                src={cheksteLogo}
                alt="CHEKSTE"
                className="h-7 w-auto"
              />
            </div>
            <div className="flex items-center gap-4 sm:gap-6 text-sm text-muted-foreground">
              <Link to="/terms" className="hover:text-primary transition-colors" data-testid="footer-link-terms">
                {lang === 'en' ? 'Terms of Service' : 'Términos de Servicio'}
              </Link>
              <span aria-hidden="true">·</span>
              <Link to="/privacy" className="hover:text-primary transition-colors" data-testid="footer-link-privacy">
                {lang === 'en' ? 'Privacy Policy' : 'Política de Privacidad'}
              </Link>
              <span aria-hidden="true">·</span>
              <Link to="/contact" className="hover:text-primary transition-colors" data-testid="footer-link-contact">
                {lang === 'en' ? 'Contact' : 'Contacto'}
              </Link>
            </div>
          </div>
          <div className="text-center text-sm text-muted-foreground mt-8">
            © 2025 CHEKSTE. {lang === 'en' ? 'All rights reserved.' : 'Todos los derechos reservados.'}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
